import { useState } from 'react'
import { type GQLError, type DefaultContext } from '@egw/clients'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'

interface UseMutationOptions<TInput, TResponse> {
  onSuccess?: (_data: TResponse, _input?: TInput) => void
  onError?: (_error: GQLError, _input?: TInput) => void
}

interface UseMutationResult<TInput, TResponse> {
  mutate: (_input: TInput, _context?: DefaultContext) => Promise<TResponse | undefined>
  isLoading: boolean
  error: GQLError | null
  isSuccess: boolean
  data: TResponse | null
}

function useMutation<TInput, TResponse>(
  execute: (_input: TInput, _context?: DefaultContext) => Promise<TResponse>,
  options: UseMutationOptions<TInput, TResponse> = {},
): UseMutationResult<TInput, TResponse> {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<GQLError | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)
  const [data, setData] = useState<TResponse | null>(null)

  const mutate = async (input: TInput, context?: DefaultContext): Promise<TResponse | undefined> => {
    setIsLoading(true)
    if (error) setError(null)
    setIsSuccess(false)
    try {
      const response = await execute(input, context)
      setData(response)
      if (options.onSuccess) options.onSuccess(response, input)
      setIsSuccess(true)
      return response
    } catch (err) {
      if (options.onError) options.onError(err as GQLError, input)
      setError(err as GQLError)
      setIsSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  return { mutate, isLoading, error, isSuccess, data }
}

/**
 * Enhanced mutation hook that automatically injects QueryClient when needed
 * @param TInput - input type
 * @param TResponse - response type
 * @param mutate - mutation function that optionally accepts QueryClient as third parameter
 */
export const createMutationHook = <TInput, TResponse>(
  mutate: (_input: TInput, _context?: DefaultContext, _queryClient?: QueryClient) => Promise<TResponse>,
) => {
  return (options?: {
    onSuccess?: (_data: TResponse, _input?: TInput) => void
    onError?: (_error: GQLError, _input?: TInput) => void
  }) => {
    const queryClient = useQueryClient()

    return useMutation<TInput, TResponse>((input, context) => mutate(input, context, queryClient), options)
  }
}
